import { FormEvent, useState } from 'react';
import { toast } from 'react-toastify';
import {
  SalesProductAzureLink,
  SalesProductAzureLinkResolvePayload,
  SalesProductLinkedApplication,
} from '../../services/salesProductWorkspaceApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

type ProductAzureLinksPanelProps = {
  applications: SalesProductLinkedApplication[];
  links: SalesProductAzureLink[];
  canWrite: boolean;
  onLink: (payload: SalesProductAzureLinkResolvePayload) => Promise<void>;
  onRefresh: (linkId: string) => Promise<void>;
  onDelete: (linkId: string) => Promise<void>;
};

const KIND_OPTIONS = [
  { value: 'WorkItem', label: 'Work item' },
  { value: 'PullRequest', label: 'Pull request' },
];

function kindLabel(kind: string): string {
  return KIND_OPTIONS.find((option) => option.value === kind)?.label ?? kind;
}

export function ProductAzureLinksPanel({
  applications,
  links,
  canWrite,
  onLink,
  onRefresh,
  onDelete,
}: ProductAzureLinksPanelProps) {
  const [applicationId, setApplicationId] = useState(applications[0]?.applicationId ?? '');
  const [kind, setKind] = useState('WorkItem');
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    if (!applicationId || !input.trim()) return;

    setSaving(true);
    try {
      await onLink({ applicationId, input: input.trim(), kind });
      setInput('');
      toast.success('Vínculo consultado no Azure DevOps.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao consultar o Azure DevOps.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-3 space-y-2 rounded-lg border border-secondary-200 bg-secondary-50 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">Azure DevOps</p>
      {applications.length === 0 ? (
        <p className="text-sm text-secondary-600">
          Vincule uma aplicação a este produto em Tecnologia → Sistemas. A aplicação precisa ter
          organização, projeto e o segredo (PAT) do Azure DevOps.
        </p>
      ) : null}

      {links.length > 0 ? (
        <ul className="space-y-2">
          {links.map((link) => (
            <li key={link.id} className="flex flex-wrap items-start justify-between gap-2 text-sm">
              <div>
                <p className="font-medium text-secondary-900">
                  {kindLabel(link.kind)} {link.externalId}
                  {link.applicationName ? ` · ${link.applicationName}` : ''}
                </p>
                <p className="text-secondary-700">{link.title}</p>
                <p className="text-xs text-secondary-500">{link.state || 'Sem estado'}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                {link.url ? (
                  <a className="text-xs text-primary-700 hover:underline" href={link.url} target="_blank" rel="noreferrer">
                    Abrir
                  </a>
                ) : null}
                {canWrite ? (
                  <>
                    <button
                      type="button"
                      className="text-xs text-secondary-700 hover:underline"
                      onClick={() =>
                        void onRefresh(link.id).catch((error) =>
                          toast.error(getRequestErrorMessage(error, 'Falha ao atualizar o vínculo.')),
                        )
                      }
                    >
                      Atualizar
                    </button>
                    <button
                      type="button"
                      className="text-xs text-red-700 hover:underline"
                      onClick={() =>
                        void onDelete(link.id).catch((error) =>
                          toast.error(getRequestErrorMessage(error, 'Falha ao remover o vínculo.')),
                        )
                      }
                    >
                      Remover
                    </button>
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {canWrite && applications.length > 0 ? (
        <form className="grid gap-2 md:grid-cols-[minmax(0,1fr)_10rem_minmax(0,1.4fr)_auto]" onSubmit={(event) => void handleSubmit(event)}>
          <label className="erp-label">
            Aplicação
            <select
              className="erp-input mt-1"
              value={applicationId}
              onChange={(event) => setApplicationId(event.target.value)}
            >
              {applications.map((application) => (
                <option key={application.applicationId} value={application.applicationId}>
                  {application.name}
                </option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Tipo
            <select className="erp-input mt-1" value={kind} onChange={(event) => setKind(event.target.value)}>
              {KIND_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Id ou URL
            <input
              className="erp-input mt-1"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="12345 ou URL do Azure DevOps"
            />
          </label>
          <div className="flex items-end">
            <button type="submit" className="erp-btn-secondary" disabled={saving || !input.trim()}>
              {saving ? 'Consultando...' : 'Vincular'}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
